import authManager from 'utils/authManager/authManager';

import { exerciseIdForRestApi } from './exerciseIdForRestApi';

const CHAT_PATH = (exerciseUuid: string) => `exercises/exercises/${exerciseUuid}/chat/`;

export type StreamExerciseChatHandlers = {
  onDelta: (chunk: string) => void;
  /** When the stream signals an error (e.g. `event: error` or JSON `detail` / `error`). */
  onStreamError?: (message: string) => void;
  signal?: AbortSignal;
};

const getAuthHeader = (): string | undefined => {
  const token = authManager.getCookieAuthData()?.token;
  return token ? `JWT ${token}` : undefined;
};

function stringifyDetail(detail: unknown): string | null {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'number' || typeof detail === 'boolean') return String(detail);
  if (Array.isArray(detail)) {
    const parts = detail.map(item => {
      if (item && typeof item === 'object' && 'message' in item) {
        return String((item as { message?: unknown }).message);
      }
      try {
        return JSON.stringify(item);
      } catch {
        return String(item);
      }
    });
    const joined = parts.filter(Boolean).join('; ');
    return joined || null;
  }
  if (typeof detail === 'object') {
    const o = detail as { message?: unknown };
    if (typeof o.message === 'string') return o.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  return null;
}

/** Reads API-style error objects (DRF, OpenAI error envelope, etc.). */
function extractJsonApiErrorMessage(j: Record<string, unknown>): string | null {
  const d = stringifyDetail(j.detail);
  if (d) return d;
  if (typeof j.error === 'string') return j.error;
  if (j.error && typeof j.error === 'object') {
    const eo = j.error as Record<string, unknown>;
    const fromNested = stringifyDetail(eo.detail) ?? (typeof eo.message === 'string' ? eo.message : null);
    if (fromNested) return fromNested;
  }
  if (typeof j.message === 'string' && !Array.isArray(j.choices)) {
    return j.message;
  }
  return null;
}

function jsonErrorMessageFromString(raw: string): string | null {
  const s = raw.trim();
  if (!s.startsWith('{')) return null;
  try {
    return extractJsonApiErrorMessage(JSON.parse(s) as Record<string, unknown>);
  } catch {
    return null;
  }
}

function extractChoiceDeltaContent(j: Record<string, unknown>): string | null {
  const choices = j.choices as Array<{ delta?: { content?: string | null }; message?: { content?: string | null } }> | undefined;
  const d0 = choices?.[0];
  const fromChoice = d0?.delta?.content ?? d0?.message?.content;
  if (typeof fromChoice === 'string' && fromChoice.length > 0) return fromChoice;

  if (typeof j.content === 'string' && j.content.length > 0) return j.content;
  if (typeof j.text === 'string' && j.text.length > 0) return j.text;
  if (typeof j.message === 'string' && j.message.length > 0 && Array.isArray(j.choices)) {
    return j.message;
  }
  return null;
}

type ClassifiedEmit = { kind: 'delta'; text: string } | { kind: 'error'; message: string } | { kind: 'noop' };

function classifyStreamDataPayload(payload: string, eventType: string | null): ClassifiedEmit {
  const trimmed = payload.trim();
  if (!trimmed) return { kind: 'noop' };

  if (eventType === 'error') {
    const fromJson = jsonErrorMessageFromString(trimmed);
    return { kind: 'error', message: fromJson && fromJson.length > 0 ? fromJson : trimmed || 'Wystąpił błąd strumienia odpowiedzi.' };
  }

  if (trimmed.startsWith('{')) {
    try {
      const j = JSON.parse(trimmed) as Record<string, unknown>;
      const token = extractChoiceDeltaContent(j);
      if (token !== null) {
        return { kind: 'delta', text: token };
      }
      const errMsg = extractJsonApiErrorMessage(j);
      if (errMsg) {
        return { kind: 'error', message: errMsg };
      }
    } catch {
      return { kind: 'noop' };
    }
    return { kind: 'noop' };
  }

  return { kind: 'delta', text: trimmed };
}

type ParsedSseBlock = { event: string | null; dataPayloads: string[] };

function parseSseEventBlock(eventBlock: string): ParsedSseBlock {
  const lines = eventBlock.split('\n');
  let event: string | null = null;
  const dataPayloads: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i];
    const line = rawLine.replace(/\r$/, '').trimStart();
    /* eslint-disable no-continue -- iterators/generators require regenerator-runtime, which is too heavyweight for this guide to allow them. Separately, loops should be avoided in favor of array iterations. */
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
      /* eslint-disable no-continue -- iterators/generators require regenerator-runtime, which is too heavyweight for this guide to allow them. Separately, loops should be avoided in favor of array iterations. */
      continue;
    }
    if (line.startsWith('data:')) {
      dataPayloads.push(line.slice(5).trimStart());
    }
  }
  return { event, dataPayloads };
}

/**
 * POSTs to the exercise chat endpoint and parses an SSE-style stream (`data: ...` lines until `data: [DONE]`).
 */
export async function streamExerciseChat(
  exerciseId: string,
  message: string,
  { onDelta, onStreamError, signal }: StreamExerciseChatHandlers,
): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  if (!baseUrl) {
    throw new Error('REACT_APP_API_URL is not configured');
  }

  const authorization = getAuthHeader();
  if (!authorization) {
    throw new Error('Not authenticated');
  }

  const restExerciseUuid = exerciseIdForRestApi(exerciseId);
  if (!restExerciseUuid) {
    throw new Error('Could not resolve exercise UUID for chat');
  }

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const url = `${normalizedBase}${CHAT_PATH(restExerciseUuid)}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({ message }),
    signal,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const text = await response.text();
      if (text) detail = text.slice(0, 500);
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Request failed with ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const emitStreamError = (errorMessage: string, raw?: string) => {
    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console -- surface stream protocol errors while developing */
      console.error('[streamExerciseChat]', errorMessage, raw !== undefined ? { raw } : '');
      /* eslint-enable no-console */
    }
    onStreamError?.(errorMessage);
  };

  const applyEmit = (emit: ClassifiedEmit): void => {
    if (emit.kind === 'error') {
      emitStreamError(emit.message);
    } else if (emit.kind === 'delta' && emit.text.length > 0) {
      onDelta(emit.text);
    }
  };

  const processPayloadList = (payloads: string[], eventType: string | null): boolean => {
    for (let i = 0; i < payloads.length; i += 1) {
      const p = payloads[i];
      const trimmed = p.trim();
      if (trimmed === '[DONE]') {
        return false;
      }

      if (/^\[ERROR\]$/i.test(trimmed)) {
        const next = payloads[i + 1];
        const nextTrim = next?.trim() ?? '';
        if (next && nextTrim.startsWith('{')) {
          const msg = jsonErrorMessageFromString(nextTrim) ?? 'Wystąpił błąd po stronie asystenta.';
          emitStreamError(msg, nextTrim);
          i += 1;
        } else {
          emitStreamError('Wystąpił błąd po stronie asystenta.', trimmed);
        }
        /* eslint-disable no-continue -- iterators/generators require regenerator-runtime, which is too heavyweight for this guide to allow them. Separately, loops should be avoided in favor of array iterations. */
        continue;
        /* eslint-enable no-continue */
      }

      const emit = classifyStreamDataPayload(p, eventType);
      if (emit.kind === 'error') {
        emitStreamError(emit.message, trimmed);
      } else {
        applyEmit(emit);
      }
    }
    return true;
  };

  const processBuffer = (): boolean => {
    buffer = buffer.replace(/\r\n/g, '\n');

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const sep = buffer.indexOf('\n\n');
      if (sep === -1) break;
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const { event, dataPayloads } = parseSseEventBlock(block);
      if (!processPayloadList(dataPayloads, event)) {
        return false;
      }
    }

    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    const linePayloads: string[] = [];
    for (let i = 0; i < lines.length; i += 1) {
      const trimmedLine = lines[i].replace(/\r$/, '').trimStart();
      if (trimmedLine.startsWith('data:')) {
        linePayloads.push(trimmedLine.slice(5).trimStart());
      }
    }
    if (!processPayloadList(linePayloads, null)) {
      return false;
    }
    return true;
  };

  try {
    /* eslint-disable no-await-in-loop -- read stream chunk-by-chunk */
    let reading = true;
    while (reading) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        processBuffer();
        reading = false;
      } else {
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: true });
        if (!processBuffer()) {
          reading = false;
        }
      }
    }
    /* eslint-enable no-await-in-loop */
  } finally {
    reader.releaseLock();
  }
}
